import { useEffect } from 'react';
import type React from 'react';

import { Alert, CommentCard, Text } from '@change/ds-core-ui';

import { useNow } from '@change-corgi/core-react/date';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';

import { usePetitionActionsPageData, usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import type { SupporterVoice } from '#src/app/pages/actions/shared/types';
import { useCurrentUser } from '#src/app/shared/hooks/session';

import { ViewMoreSupporterVoices } from './components/ViewMoreSupporterVoices';
import { useLike } from './hooks/useLike';
import { useNextPage } from './hooks/useNextPage';
import { useUnlike } from './hooks/useUnlike';

export const SupporterVoices: React.FC = () => {
	const currentUser = useCurrentUser();
	const {
		data: { petition },
	} = usePetitionActionsPageData();
	const [{ supporterVoices, unlikeErrors, likeErrors }] = usePetitionActionsPageState();
	const {
		actions: { nextPage },
	} = useNextPage();
	const {
		actions: { like },
	} = useLike();
	const {
		actions: { unlike },
	} = useUnlike();
	const now = useNow();
	const { translate, localizeRelativeTime, localizeNumber } = useI18n();
	const navigate = useNavigate();

	useEffect(() => {
		void nextPage();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (supporterVoices.length === 0) {
		return null;
	}

	return (
		<div data-testid="supporter-voices">
			<Text as="h2" variant="heading" size="medium" fontWeight="bold" className="text-typography-strong my-4 pl-2.5">
				<Translate value="corgi.petition_actions.supporter_voices_text_list.heading" />
			</Text>
			{supporterVoices.map(({ id, user, text, likeCount, viewerLiked, createdAt }: SupporterVoice) => (
				<div key={id} className="mb-3">
					<CommentCard
						data-testid={`action-card-${id}`}
						commentContent={text}
						userName={user.shortDisplayName}
						userLocation={localizeRelativeTime(now, new Date(createdAt))}
						liked={viewerLiked}
						likeCounter={likeCount > 0 ? localizeNumber(likeCount) : undefined}
						likeButtonAriaLabel={translate('corgi.petition_actions.supporter_voices_text_list.like_button_aria_label')}
						fullCommentLinkLabel={translate('corgi.petition_actions.supporter_voices_text_list.read_more_label')}
						likeButtonDataTestId={`like-button-${id}`}
						maxCommentLines={3}
						onLikeChanged={async (nextLiked) => {
							if (!currentUser) {
								navigate(`/login_or_join?redirectTo=/p/${petition.slug}/actions`);
								return;
							}
							if (nextLiked) {
								await like(id);
							} else {
								await unlike(id);
							}
						}}
					/>
					{likeErrors[id] && (
						<Alert variant="error" className="mt-1 text-sm">
							<Translate value="corgi.petition_actions.supporter_voices_text_list.errors.like_add" />
						</Alert>
					)}
					{unlikeErrors[id] && (
						<Alert variant="error" className="mt-1 text-sm">
							<Translate value="corgi.petition_actions.supporter_voices_text_list.errors.like_remove" />
						</Alert>
					)}
				</div>
			))}
			<div className="mt-8">
				<ViewMoreSupporterVoices />
			</div>
		</div>
	);
};
